import invariant from "tiny-invariant";

import User from "@mapmycustomers/shared/types/User";

import { isManager, isOwner } from "@app/store/iam/util";
import Scope from "@app/types/dashboard/Scope";

// See a note in the code about return types
const canEditBoard = (
  currentUser: User,
  scope: Scope,
  action:
    | "add"
    | "delete"
    | "editCardConfiguration"
    | "editCardGoal"
    | "editCardPreferences"
    | "editLayout"
    | "editSettings"
): boolean | undefined => {
  if (!scope.teamId && !scope.userId) {
    // the entire org scope
    // => only owners can edit
    return isOwner(currentUser);
  } else if (scope.teamId && !scope.userId) {
    // team board
    // => only owners or managers _of that team_ can edit
    // Note: the part "of that team" is not checked here because managers of other teams won't even
    // see this dashboard
    return isOwner(currentUser) || isManager(currentUser);
  } else if (!scope.teamId && scope.userId) {
    // either an owner board or a member without a team
    // => either org owner or board owner can edit it
    return isOwner(currentUser) || currentUser.id === scope.userId;
  } else {
    // rep's board

    // We forbid adding cards from rep board, except when it's manager's own board
    if (action === "add") {
      return isManager(currentUser) && scope.userId === currentUser.id;
    }

    // => either owner or manager can edit the board, reps can only edit some details, check action
    // Note: Owners & Managers won't see rep's own dashboards normally, they'll see a team dashboard
    // with user data in it.
    if (isOwner(currentUser) || isManager(currentUser)) {
      return true;
    }

    // reps can't change board configuration or individual charts configuration...
    if (["delete", "editCardConfiguration", "editLayout"].includes(action)) {
      return false;
    }

    // ...except preferences, but only if this is their board
    if (
      ["editCardPreferences", "editSettings"].includes(action) &&
      currentUser.id === scope.userId
    ) {
      return true;
    }

    if (action === "editCardGoal") {
      // undefined means that we can't detect if user is or is not able to
      // modify the goal, because they're only allowed to do that if team board
      // doesn't have goal set. And we don't know if this is true or false from here.
      // So we return undefined to let caller figure this out themselves.
      return currentUser.id !== scope.userId ? false : undefined;
    }

    invariant(false, `Unknown action requested: ${action}`);
  }
};

export default canEditBoard;
